/**
 * shareVerseImage.js, Generate a square branded image (1080×1080) of a Bible
 * verse via HTML5 Canvas, then either share it via the Web Share API
 * (mobile/PWA) or trigger a download (desktop fallback).
 *
 * Visual identity: navy #0D1B2A background, gold #C9A961 accents, Fraunces
 * (display) for the verse + Manrope (sans) for metadata. Includes app
 * branding + @ccarlosmedeiross handle.
 */

const NAVY = "#0D1B2A";
const NAVY_LIGHT = "#16263A";
const GOLD = "#C9A961";
const PARCHMENT = "#F4ECDC";

const SIZE = 1080;
const MARGIN = 96;

/* ----------------------------- Helpers ----------------------------- */

const wrapText = (ctx, text, maxWidth) => {
  // Greedy word-wrap. Returns array of lines (string[]).
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const tentative = current ? `${current} ${w}` : w;
    if (ctx.measureText(tentative).width <= maxWidth) {
      current = tentative;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
};

/** Pick a font size that makes the verse fit at most `maxLines` lines. */
const fitFontSize = (ctx, text, maxWidth, maxLines, weight = "400") => {
  // Try sizes from large to small. Fraunces is loaded by the app.
  const sizes = [80, 72, 64, 58, 54, 50, 46, 42, 38, 34, 30];
  for (const s of sizes) {
    ctx.font = `italic ${weight} ${s}px "Fraunces", serif`;
    const lines = wrapText(ctx, text, maxWidth);
    if (lines.length <= maxLines) return { size: s, lines };
  }
  ctx.font = `italic ${weight} 30px "Fraunces", serif`;
  const lines = wrapText(ctx, text, maxWidth);
  return { size: 30, lines };
};

const drawCornerOrnament = (ctx, x, y, size, color) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  // Simple gold corner: two small lines + a dot
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(size, 0);
  ctx.moveTo(0, 0);
  ctx.lineTo(0, size);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

/* --------------------------- Main render --------------------------- */

/**
 * Render a verse card to a HTMLCanvasElement and return it.
 * @param {{ text: string, reference: string, brand?: string, handle?: string }} opts
 */
export const buildVerseCanvas = async ({
  text,
  reference,
  brand = "Bíblia com Propósito",
  handle = "@ccarlosmedeiross",
}) => {
  // Make sure the custom fonts are ready (Fraunces, Manrope) before drawing.
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch { /* non-blocking */ }
  }

  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  // ---- Background gradient ----
  const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  grad.addColorStop(0, NAVY);
  grad.addColorStop(1, NAVY_LIGHT);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Subtle radial glow top-left (navy/gold flare)
  const flare = ctx.createRadialGradient(180, 180, 60, 180, 180, 600);
  flare.addColorStop(0, "rgba(201, 169, 97, 0.18)");
  flare.addColorStop(1, "rgba(201, 169, 97, 0)");
  ctx.fillStyle = flare;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // ---- Inner gold frame ----
  ctx.strokeStyle = "rgba(201, 169, 97, 0.55)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(40, 40, SIZE - 80, SIZE - 80);

  // ---- Corner ornaments ----
  const oSize = 28;
  const oOff = 60;
  drawCornerOrnament(ctx, oOff, oOff, oSize, GOLD);
  ctx.save(); ctx.translate(SIZE - oOff, oOff); ctx.rotate(Math.PI / 2);
  drawCornerOrnament(ctx, 0, 0, oSize, GOLD); ctx.restore();
  ctx.save(); ctx.translate(SIZE - oOff, SIZE - oOff); ctx.rotate(Math.PI);
  drawCornerOrnament(ctx, 0, 0, oSize, GOLD); ctx.restore();
  ctx.save(); ctx.translate(oOff, SIZE - oOff); ctx.rotate(-Math.PI / 2);
  drawCornerOrnament(ctx, 0, 0, oSize, GOLD); ctx.restore();

  // ---- Brand pill (top) ----
  ctx.font = '500 22px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = GOLD;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(brand.toUpperCase(), SIZE / 2, 140);

  // small tracking divider underneath
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(SIZE / 2 - 30, 170);
  ctx.lineTo(SIZE / 2 + 30, 170);
  ctx.stroke();

  // ---- Verse text (centered, italic Fraunces) ----
  const maxWidth = SIZE - MARGIN * 2;
  const { size: fontSize, lines } = fitFontSize(ctx, `“${text}”`, maxWidth, 8, "400");

  ctx.fillStyle = PARCHMENT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const lineHeight = fontSize * 1.35;
  const totalHeight = lines.length * lineHeight;
  const startY = SIZE / 2 - totalHeight / 2 + lineHeight / 2 - 30; // shift up for ref+handle below
  lines.forEach((line, i) => {
    ctx.fillText(line, SIZE / 2, startY + i * lineHeight);
  });

  // ---- Reference (bottom) ----
  const refY = SIZE - 230;
  ctx.font = '600 36px "Fraunces", serif';
  ctx.fillStyle = GOLD;
  ctx.fillText(reference, SIZE / 2, refY);

  // ---- Handle / footer ----
  ctx.font = '500 20px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(244, 236, 220, 0.75)";
  ctx.fillText(handle, SIZE / 2, SIZE - 120);

  // Tiny domain
  ctx.font = '500 16px "Manrope", system-ui, sans-serif';
  ctx.fillStyle = "rgba(201, 169, 97, 0.55)";
  ctx.fillText("app.cemedeiros.com.br", SIZE / 2, SIZE - 88);

  return canvas;
};

/** Render and return a Blob (image/jpeg, quality 0.92). */
export const buildVerseBlob = async (opts) => {
  const canvas = await buildVerseCanvas(opts);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("canvas toBlob failed"))),
      "image/jpeg",
      0.92,
    );
  });
};

/**
 * Try to share a generated image via Web Share API (mobile/PWA). If
 * unsupported, download the file. Returns "shared" | "downloaded" | "failed".
 *
 * Also writes a text fallback ("verse, reference\nlink") to the share payload
 * so the receiver can copy/paste the textual citation.
 */
export const shareVerseImage = async ({ text, reference, url }) => {
  let blob;
  try {
    blob = await buildVerseBlob({ text, reference });
  } catch {
    return "failed";
  }

  const fileName = `versiculo-${reference.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}.jpg`;
  const file = new File([blob], fileName, { type: "image/jpeg" });

  // Web Share Level 2 (with files)
  const shareData = {
    title: reference,
    text: `“${text}”, ${reference}`,
    url: url || (typeof window !== "undefined" ? window.location.href : ""),
    files: [file],
  };

  if (
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] }) &&
    typeof navigator.share === "function"
  ) {
    try {
      await navigator.share(shareData);
      return "shared";
    } catch {
      // user cancelled, fall through to download
    }
  }

  // Desktop fallback: trigger a download
  try {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 1000);
    return "downloaded";
  } catch {
    return "failed";
  }
};
